import { Formik } from "formik";
import SearchField from "../../../forms/search-field";
import { useDispatch } from "react-redux";
import { purchaseProductListAction } from "../../../../redux/actions/purchase-action";
import { getVendorMappedAction } from "../../../../redux/actions/settings-action";
import DynamicTransactionTable from "../../../tables/dynamic-transaction-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { customerListAction } from "../../../../redux/actions/general-action";

const ViewVendorCatalouge = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [tableBodyData, setTableBodyData] = useState([]);
  const [reload, setReload] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dataCount, setDataCount] = useState(0);

  const [vendorId, setVendorId] = useState(null);
  const [itemCode, setItemCode] = useState(null);

  const initialValues = {
    system_item_code: null,
    system_item_name: null,
    cmr_code: null,
    cmr_name: null,
    cmr_id: null,
  };

  const fetchMappedData = useCallback(
    (vId, iCode) => {
      setLoading(true);
      dispatch(
        getVendorMappedAction(
          { vendor_id: vId ?? null, item_code: iCode ?? null },
          (res) => {
            const rows = (res?.data || []).map((r, idx) => ({
              id: idx,
              vendor_code: r.vendor_code,
              vendor_name: r.vendor_name,
              vendor_item_code: r.vendor_item_code,
              vendor_item_name: r.vendor_item_name,
              vendor_uom: r.vendor_uom,
              system_item_code: r.system_item_code,
              system_item_name: r.system_item_name,
              system_item_uom: r.system_item_uom,
            }));
            setTableBodyData(rows);
            setDataCount(rows.length);
            setLoading(false);
          },
          () => {
            setTableBodyData([]);
            setLoading(false);
          }
        )
      );
    },
    [dispatch]
  );

  useEffect(() => {
    fetchMappedData(null, null);
  }, [fetchMappedData]);

  useEffect(() => {
    fetchMappedData(vendorId, itemCode);
  }, [vendorId, itemCode, fetchMappedData]);

  const paginationProps = {
    pageSize,
    setPageSize,
    dataCount,
    setCurrentPage,
    currentPage,
  };

  const heading = useMemo(
    () => [
      { label: "Vendor Code", value: "vendor_code", minWidth: 120, column: true, display: true, enabled: true },
      { label: "Vendor Name", value: "vendor_name", minWidth: 160, column: true, display: true, enabled: true },
      { label: "Vendor Item Code", value: "vendor_item_code", minWidth: 140, column: true, display: true, enabled: true },
      { label: "Vendor Item Name", value: "vendor_item_name", minWidth: 180, column: true, display: true, enabled: true },
      { label: "Vendor UOM", value: "vendor_uom", minWidth: 120, column: true, display: true, enabled: true },
      { label: "System Item Code", value: "system_item_code", minWidth: 140, column: true, display: true, enabled: true },
      { label: "System Item Name", value: "system_item_name", minWidth: 200, column: true, display: true, enabled: true },
      { label: "System Item UOM", value: "system_item_uom", minWidth: 160, column: true, display: true, enabled: true },
    ],
    []
  );

  const loadSystemOptions = (inputValue, field) =>
    new Promise((resolve) => {
      if (!inputValue) return resolve([]);
      dispatch(
        purchaseProductListAction(
          { field, value: inputValue },
          (data) =>
            resolve(
              (data?.data || []).map((it) => ({
                value: it.item_id,
                label: field === "item_code" ? it.item_code : it.item_name,
                raw: it,
              }))
            ),
          () => resolve([])
        )
      );
    });

  const [vendorOptionsData, setVendorOptionsData] = useState([]);
  const loadVendorOptions = (inputValue, field) =>
    new Promise((resolve) => {
      if (!inputValue) return resolve([]);
      dispatch(
        customerListAction(
          { field, value: inputValue, isVendor: true },
          (data) => {
            setVendorOptionsData(data.data || []);
            resolve(
              (data.data || []).map((item) => ({
                value: item.cmr_id,
                label: `${item?.cmr_code}-${item?.cmr_first_name}`,
              }))
            );
          },
          () => resolve([])
        )
      );
    });

  return (
    <div className="h-full flex flex-col">
      <Formik initialValues={initialValues} onSubmit={() => {}}>
        {({ setFieldValue }) => (
          <div className="flex flex-row justify-around gap-4">
            <div className="min-w-0 w-[220px]">
              <SearchField
                type="text"
                label="Vendor Code"
                menuPortalTarget={document.body}
                name="cmr_code"
                placeholder="Enter Vendor Code"
                loadOptions={(e) => loadVendorOptions(e, "cmr_code")}
                onChange={(opt, meta) => {
                  if (meta?.action === "clear") {
                    setVendorId(null);
                    setFieldValue("cmr_id", null);
                    return;
                  }
                  setVendorId(opt?.value ?? null);
                  setFieldValue("cmr_id", opt?.value ?? null);
                }}
              />
            </div>

            <div className="min-w-0 w-[260px]">
              <SearchField
                type="text"
                label="Vendor Name"
                menuPortalTarget={document.body}
                name="cmr_name"
                placeholder="Enter Vendor Name"
                loadOptions={(e) => loadVendorOptions(e, "cmr_first_name")}
                onChange={(opt, meta) => {
                  if (meta?.action === "clear") {
                    setVendorId(null);
                    setFieldValue("cmr_id", null);
                    return;
                  }
                  setVendorId(opt?.value ?? null);
                  setFieldValue("cmr_id", opt?.value ?? null);
                }}
              />
            </div>

            <div className="min-w-0 w-[220px]">
              <SearchField
                type="text"
                label="System Item Code"
                name="system_item_code"
                placeholder="Select System Code"
                loadOptions={(e) => loadSystemOptions(e, "item_code")}
                onChange={(opt, meta) => {
                  if (meta?.action === "clear") {
                    setItemCode(null);
                    return;
                  }
                  setItemCode(opt?.raw?.item_code ?? null);
                }}
              />
            </div>

            <div className="min-w-0 w-[260px]">
              <SearchField
                type="text"
                label="System Item Name"
                name="system_item_name"
                placeholder="Select System Name"
                loadOptions={(e) => loadSystemOptions(e, "item_name")}
                onChange={(opt, meta) => {
                  if (meta?.action === "clear") {
                    setItemCode(null);
                    return;
                  }
                  setItemCode(opt?.raw?.item_code ?? null);
                }}
              />
            </div>
          </div>
        )}
      </Formik>

      <div className="flex-1 min-h-0">
        <DynamicTransactionTable
          transaction="Business Catalogue"
          transaction_type="bussiness_catalogue"
          module={"bussiness-catalouge-setup"}
          path="bussiness-catalouge"
          filterSec={false}
          tableHeading={heading}
          tableBodyData={tableBodyData}
          paginationProps={paginationProps}
          filter={false}
          list={false}
          menu={true}
          is_checkbox_req={false}
          setLoading={setLoading}
          reload={reload}
          setReload={setReload}
          transactionKey="bc"
          date={false}
          showClear={false}
          addNew={false}
          showFilterArrows={false}
        />
      </div>
    </div>
  );
};

export default ViewVendorCatalouge;
